import { useDispatch, useSelector } from 'react-redux'
import { useAtom, useSetAtom } from 'jotai'

import { ORDER_STATUS } from 'src/components/DeliveryCard/constants'
import {
  dayByDeliveryDateSelector,
  isOrderSkippedSelector,
  orderStatusSelector
} from 'src/components/DeliveryCard/selectors'
import { useUnskipOrderInPause } from 'src/api/UnskipOrder/unskipOrderInPause'
import { UnkskiptTrackingData } from 'src/types/tracking-types'
import { orderStatusForTrackingSelector } from 'src/redux/slices/selectedDelivery/selectors'
import { fetchMyPlan } from 'src/redux-api-bridge'
import { track } from 'src/shared/tracking'
import { unskipWhenIsPausedModalAtom } from 'src/atoms/modals/unskipWhenIsPausedModal.atom'
import { unskipWhenIsPausedSuccessModalAtom } from 'src/atoms/modals/unskipWhenIsPausedSuccessModal.atom'
import { useCreateUnskip } from 'src/api/UnskipOrder/useCreateUnskip'
import { toaster } from 'src/utils/toaster'
import mobileAndTabletCheck from 'src/utils/mobileAndTabletcheck'
import { fetchCartForDate } from 'src/redux-api-bridge/cart/effects'

export function useUnskip(
  deliveryDate: string | undefined,
  trackingData: UnkskiptTrackingData | undefined = undefined
) {
  const dispatch = useDispatch()
  const orderStatus = useSelector(state =>
    orderStatusSelector(state, deliveryDate)
  )
  const delivery = useSelector(state =>
    dayByDeliveryDateSelector(state, deliveryDate)
  )
  const isOrderSkipped = useSelector(state =>
    isOrderSkippedSelector(state, deliveryDate)
  )
  const orderStatusForTracking = useSelector(orderStatusForTrackingSelector)
  const setUnskipWhenIsPausedModal = useSetAtom(unskipWhenIsPausedModalAtom)
  const [unskipSuccessModal, setUnskipSuccessModal] = useAtom(
    unskipWhenIsPausedSuccessModalAtom
  )
  const { createUnskip } = useCreateUnskip({ onCompleted: onCompletedUnskip })
  const { unskipOrderInPause } = useUnskipOrderInPause({
    onCompleted: onCompletedInPause
  })

  async function unskipIfRequired(showUnskipSuccessModal = true) {
    if (!isOrderSkipped || !delivery) {
      return
    }

    if (delivery.isPaused) {
      setUnskipWhenIsPausedModal({
        isOpen: true,
        date: delivery.displayDate
      })
      return
    }

    return unskipOrder(showUnskipSuccessModal)
  }

  async function unskipOrderOnPlanPaused({ unpause = true }) {
    setUnskipWhenIsPausedModal({
      isOpen: false,
      date: ''
    })

    if (unpause) {
      unskipOrder()
    } else {
      unskipOrderInPause({ deliveryDate: delivery?.date })
      setUnskipSuccessModal({
        ...unskipSuccessModal,
        date: delivery.date,
        displayDate: delivery.displayDate,
        type: 'unskip'
      })
    }
  }

  async function unskipOrder(showUnskipSuccessModal = true) {
    if (showUnskipSuccessModal) {
      setUnskipSuccessModal({
        ...unskipSuccessModal,
        date: delivery.date,
        displayDate: delivery.displayDate,
        type: 'unskip'
      })
    }
    const origin = getUnskipOrigin()
    return createUnskip({ unskip: { date: delivery.date }, origin })
  }

  function trackUnskipOrder() {
    if (orderStatus === ORDER_STATUS.PAUSED) {
      track('Subscription Unpaused', {
        unpause_source: 'unskip',
        delivery_date: deliveryDate
      })
    } else {
      const tags = trackingData || {}
      track('Unskip Day', {
        ...tags,
        delivery_date: deliveryDate,
        order_status: orderStatusForTracking
      })
    }
  }

  function getUnskipOrigin() {
    return mobileAndTabletCheck()
      ? 'customer_web_mobile'
      : 'customer_web_desktop'
  }

  function onCompletedInPause() {
    dispatch(fetchMyPlan)
    setUnskipSuccessModal({ ...unskipSuccessModal, isOpen: true })
    trackUnskipOrder()
  }

  function onCompletedUnskip() {
    dispatch(fetchMyPlan)
    dispatch(fetchCartForDate(deliveryDate))
    toaster.success('Your order has been unskipped!')
    trackUnskipOrder()
  }

  return {
    unskipIfRequired,
    unskipOrderOnPlanPaused
  }
}
